import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { ConfirmDialog } from "@/components/dialogs";
import { AppearanceDialog } from "@/components/appearance/AppearanceDialog";

export function BoardManager({ onChanged, inline }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [stages, setStages] = useState("To Do, Doing, Waiting, Review, Done");
  const create = async () => {
    if (!name.trim()) return toast.error("Please name the board.");
    try {
      await apiFetch("/tasks/boards", { method: "POST", body: JSON.stringify({ name: name.trim(), stages: stages.split(",").map((s) => s.trim()).filter(Boolean) }) });
      toast.success("Board created"); setOpen(false); setName(""); onChanged();
    } catch (e) { toast.error(e.message); }
  };
  return (
    <>
      {inline
        ? <Button size="sm" onClick={() => setOpen(true)} data-testid="new-board-btn" className="gap-1.5"><Plus className="h-4 w-4" /> New board</Button>
        : <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setOpen(true)} data-testid="new-board-btn"><Plus className="h-4 w-4" /> New board</Button>}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="board-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New board</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Board name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reel Production" data-testid="board-name" /></div>
            <div><Label className="text-xs">Stages (comma separated)</Label><Input value={stages} onChange={(e) => setStages(e.target.value)} data-testid="board-stages" /></div>
            <p className="text-[11px] text-muted-foreground">Personal board stages are separate from official ContentOS workflow statuses.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={create} data-testid="board-create">Create board</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function BoardActions({ board, onChanged, onArchived }) {
  const [renameOpen, setRenameOpen] = useState(false);
  const [name, setName] = useState(board.name);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [appOpen, setAppOpen] = useState(false);
  const rename = async () => {
    if (!name.trim()) return toast.error("Please name the board.");
    try { await apiFetch(`/tasks/boards/${board.id}`, { method: "PUT", body: JSON.stringify({ name: name.trim(), stages: board.stages }) }); toast.success("Board renamed"); setRenameOpen(false); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const saveAppearance = async ({ icon, colour }) => {
    try { await apiFetch(`/tasks/boards/${board.id}`, { method: "PUT", body: JSON.stringify({ name: board.name, stages: board.stages, icon, accent: colour }) }); toast.success("Board appearance updated"); onChanged(); }
    catch (e) { toast.error(e.message); }
  };
  const archive = async () => {
    try { await apiFetch(`/tasks/boards/${board.id}/archive`, { method: "PUT", body: JSON.stringify({ archived: true }) }); toast.success("Board archived"); setArchiveOpen(false); onArchived(); }
    catch (e) { toast.error(e.message); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button className="rounded-lg border border-border px-2 py-1.5 text-muted-foreground hover:bg-muted" data-testid="board-actions" title="Board options"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-44">
          <DropdownMenuItem onClick={() => { setName(board.name); setRenameOpen(true); }} data-testid="board-rename">Rename board</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setAppOpen(true)} data-testid="board-appearance">Board appearance</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setArchiveOpen(true)} data-testid="board-archive">Archive board</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AppearanceDialog open={appOpen} onOpenChange={setAppOpen} kind="header"
        title={`Board appearance — ${board.name}`} label={board.name}
        initial={{ icon: board.icon, colour: board.accent }} onSave={saveAppearance} />
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm" data-testid="board-rename-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Rename board</DialogTitle></DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="board-rename-input" />
          <DialogFooter><Button variant="outline" onClick={() => setRenameOpen(false)}>Cancel</Button><Button onClick={rename} data-testid="board-rename-save">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={archiveOpen} onOpenChange={setArchiveOpen} title={`Archive “${board.name}”?`}
        description="The board is hidden from your board list. Tasks on it are kept and stay in your other views." confirmLabel="Archive board" onConfirm={archive} />
    </>
  );
}
